import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid2 as Grid,
  TextField,
  MenuItem,
  Paper,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  CircularProgress,
  Autocomplete,
  Tooltip,
  InputAdornment,
  Divider,
  Popover,
  List as MuiList,
  ListItemButton,
} from '@mui/material';
import {
  Business,
  LocationOn,
  ContactMail,
  AdminPanelSettings,
  Add,
  Refresh,
  FileDownload,
  CheckCircle,
  Cancel,
  Edit,
  Phone,
  Email,
  AccountBalance,
  Payment,
  Close,
  Settings,
  Store,
  Save,
  PowerSettingsNew,
  CreditCard,
  Link as LinkIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  VpnKey,
  ContentCopy,
  CallMerge,
  SwapHoriz,
  AccountBalanceWallet,
  PersonSearch,
  PersonAdd,
  Visibility,
  VisibilityOff,
  Autorenew,
  Search as SearchIcon,
  Gavel as ContratoIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Snackbar from '@mui/material/Snackbar';
import TableContainer from '@mui/material/TableContainer';
import Alert from '@mui/material/Alert';
import DataTable, { Column, StatusChip, DateCell, CurrencyCell } from '../../../components/Common/DataTable';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { todayCv } from '../../../utils/date';

// Convert Buffer object from API to base64 string
const bufferToBase64 = (buffer: any): string | null => {
  if (!buffer) return null;
  if (typeof buffer === 'string') return buffer;
  if (buffer.type === 'Buffer' && Array.isArray(buffer.data)) {
    let binary = '';
    for (let i = 0; i < buffer.data.length; i++) {
      binary += String.fromCharCode(buffer.data[i]);
    }
    return btoa(binary);
  }
  return null;
};

interface EntityData {
  id: string;
  codigo: string;
  desig: string;
  tipo: string;
  tipoId: string;
  banco: string;
  numConta: string;
  nomeContacto: string;
  morada: string;
  telefone: string;
  email: string;
  estado: string;
  dataRegisto: string;
  servicos: number;
}

interface EntityStats {
  total: number;
  ativos: number;
  inativos: number;
  servicos: number;
}

interface EntityType {
  id: string;
  nome: string;
  codigo: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const dedupeContactTypes = (types: Array<{ id: string; codigo: string; desig: string }>) => {
  const seen = new Set<string>();
  return types.filter(t => { const k = (t.codigo || '').toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; });
};

/** 600 → "6%", 550 → "5,5%". */
const pct = (bps: number) => `${(bps / 100).toLocaleString('pt-PT', { maximumFractionDigits: 2 })}%`;

/**
 * O que uma linha de serviço cobra de facto, vindo de GET /entities/:id/services.
 * O tarifário só manda sem contrato em vigor; nos serviços pagos pela SISP a entidade
 * paga 0. Sem `cobranca` (falha no backend) mostra-se o tarifário, como antes.
 */
function CobrancaCell({ serv }: { serv: any }) {
  const c = serv.cobranca;
  const tarifa = serv.tarifarioNome
    ? `${serv.tarifarioCodigo} — ${serv.tarifarioNome}` : null;
  if (!c) {
    return tarifa
      ? <Chip label={tarifa} size="small" color="primary" variant="outlined" />
      : <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  const taxas = (c.taxasBps || []).map(pct).join(' / ');
  const rascunho = c.rascunho
    ? <Chip label={`rascunho v${c.rascunho.versao}`} size="small" color="warning" variant="outlined" sx={{ ml: 0.5 }} />
    : null;
  if (c.origem === 'CONTRATO') {
    return (
      <Box>
        <Chip size="small" color="success"
          label={`Contrato v${c.contrato.versao} · ${taxas}`
            + (c.contrato.tectoCVE != null ? ` · tecto ${c.contrato.tectoCVE.toLocaleString('pt-PT')}$` : '')} />
        {rascunho}
        {c.tarifarioIgnorado && tarifa && (
          <Typography variant="caption" color="text.secondary" display="block">
            tarifário {tarifa} — ignorado, o contrato manda
          </Typography>
        )}
      </Box>
    );
  }
  if (c.origem === 'TARIFARIO') {
    return <Box><Chip label={`${tarifa ?? 'Tarifário'} · ${taxas}`} size="small" color="primary" variant="outlined" />{rascunho}</Box>;
  }
  if (c.origem === 'SISP') {
    return (
      <Tooltip title="A Pagali é remunerada pela comissão da SISP; a entidade não paga comissão.">
        <Box component="span"><Chip label="Comissão SISP · 0% à entidade" size="small" color="info" variant="outlined" />{rascunho}</Box>
      </Tooltip>
    );
  }
  if (c.origem === 'OMISSAO') {
    return (
      <Tooltip title="Sem contrato nem tarifário: aplica-se a taxa por omissão do serviço.">
        <Box component="span"><Chip label={`${taxas} por omissão`} size="small" color="warning" variant="outlined" />{rascunho}</Box>
      </Tooltip>
    );
  }
  return tarifa
    ? <Chip label={tarifa} size="small" color="primary" variant="outlined" />
    : <Typography variant="caption" color="text.secondary">—</Typography>;
}

const Entities: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [entities, setEntities] = useState<EntityData[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [entityTypes, setEntityTypes] = useState<EntityType[]>([]);
  const [stats, setStats] = useState<EntityStats>({
    total: 0,
    ativos: 0,
    inativos: 0,
    servicos: 0,
  });

  // View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<any>(null);
  const [viewTab, setViewTab] = useState(0);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Contacts state
  const [contactTypes, setContactTypes] = useState<Array<{ id: string; codigo: string; desig: string }>>([]);
  const [addContactOpen, setAddContactOpen] = useState(false);
  const [newContact, setNewContact] = useState({ tipoCodigo: '', contacto: '' });
  const [savingContact, setSavingContact] = useState(false);

  // Edit mode state
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ desig: '', nomeContacto: '', morada: '', numConta: '', banco: '', tipoId: '', posId: '', posAuthCode: '', posUrl: '',
    nif: '', nomeResp: '', emailEntidade: '', telefone: '', website: '', geografiaId: '', bancoId: '' });
  const [bancosList, setBancosList] = useState<Array<{ id: string; nome: string }>>([]);
  const [savingEdit, setSavingEdit] = useState(false);

  // POS list for gateway config
  const [posList, setPosList] = useState<Array<{ id: string; designation: string; pos: string; posAuthCode: string; posUrl: string; environment: string; servico?: string | null }>>([]);

  // Create dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  // Mapeamento perfil → código de tipo de entidade
  const PERFIL_TIPO_MAP: Record<string, string> = {
    PFLOJ: 'LOJA', PFEMP: 'EMPRESA', PFGOV: 'GOVERNO',
    PFONG: 'ONG', PFEVT: 'EVENTOS', PFPRO: 'PROFISSIONAL', PFTUR: 'TURISMO',
  };

  const [createForm, setCreateForm] = useState({
    // Dados da Entidade
    desig: '',
    nomeResp: '',        // Nome do responsável
    nomeContacto: '',    // Nome de contacto
    perfilCodigo: '',    // Perfil de conta
    tipoId: '',          // Tipo de entidade (auto-preenchido pelo perfil)
    nif: '',             // NIF
    telefone: '',
    emailEntidade: '',   // Email institucional
    website: '',
    descricao: '',
    // Localização
    morada: '',
    geografiaId: '',
    // Banco
    banco: '',
    numConta: '',
    // Pagamento
    taxaTransacao: '',
    // Conta Admin
    email: '',           // Email de login do admin
  });
  const [geografiaList, setGeografiaList] = useState<Array<{ id: string; ilha: string; concelho: string }>>([]);
  const [savingCreate, setSavingCreate] = useState(false);

  // Logo upload
  const [uploadingLogo, setUploadingLogo] = useState(false);

  // Integration data
  const [integrationData, setIntegrationData] = useState<any[]>([]);
  const [loadingIntegration, setLoadingIntegration] = useState(false);
  const [migratingPages, setMigratingPages] = useState(false);
  const [consolidateDialogOpen, setConsolidateDialogOpen] = useState(false);
  const [consolidateName, setConsolidateName] = useState('');
  const [consolidating, setConsolidating] = useState(false);

  // Extrato state
  const [extratoData, setExtratoData] = useState<any[]>([]);
  const [extratoStats, setExtratoStats] = useState({ totalTransacoes: 0, totalBruto: 0, totalComissao: 0, totalIva: 0, totalLiquido: 0 });
  const [extratoPage, setExtratoPage] = useState(0);
  const [extratoTotal, setExtratoTotal] = useState(0);
  const [extratoLoading, setExtratoLoading] = useState(false);

  // Liquidações state
  const [liquidacoesData, setLiquidacoesData] = useState<any[]>([]);
  const [liquidacoesStats, setLiquidacoesStats] = useState({ total: 0, totalLiquidado: 0, totalPendente: 0 });
  const [liquidacoesLoading, setLiquidacoesLoading] = useState(false);

  // Webhook config state
  const [urlPopoverAnchor, setUrlPopoverAnchor] = useState<HTMLButtonElement | null>(null);
  const [knownUrls, setKnownUrls] = useState<any[]>([]);
  const [loadingKnownUrls, setLoadingKnownUrls] = useState(false);
  const [webhookConfig, setWebhookConfig] = useState<any>(null);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookAtivo, setWebhookAtivo] = useState(true);
  const [webhookLoading, setWebhookLoading] = useState(false);
  const [webhookSaving, setWebhookSaving] = useState(false);
  const [webhookSecretVisible, setWebhookSecretVisible] = useState(false);
  const [webhookRevealedSecret, setWebhookRevealedSecret] = useState('');
  const [webhookDeliveries, setWebhookDeliveries] = useState<any[]>([]);
  const [regenerating, setRegenerating] = useState(false);
  const [liquidacaoForm, setLiquidacaoForm] = useState({ montante: '', dtLiquidacao: '', dtPeriodoIni: '', dtPeriodoFim: '', referencia: '', notas: '' });
  const [savingLiquidacao, setSavingLiquidacao] = useState(false);
  const [liquidacaoFormOpen, setLiquidacaoFormOpen] = useState(false);

  // Reset password / create account state
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetCredentials, setResetCredentials] = useState<{ username: string; email: string; password: string } | null>(null);
  const [resetDialog, setResetDialog] = useState(false);
  const [creatingAccount, setCreatingAccount] = useState(false);

  // SISP state
  const [sispEnabled, setSispEnabled] = useState(false);
  const [sispForm, setSispForm] = useState({ entidadeSisp: '', tipoSisp: 'Serviço', tokenSisp: '' });
  const [savingSisp, setSavingSisp] = useState(false);

  // Pagali Pass — plano de eventos por entidade
  const [planoEventos, setPlanoEventos] = useState('STARTER');
  const [savingPlano, setSavingPlano] = useState(false);
  const [availablePlans, setAvailablePlans] = useState<Array<{ id: number; codigo: string; nome: string; descricao: string | null; destaque: boolean }>>([]);
  const [sispServices, setSispServices] = useState<any[]>([]);
  const [loadingSispServices, setLoadingSispServices] = useState(false);

  // Card types
  const [allCardTypes, setAllCardTypes] = useState<Array<{ id: string; codigo: string; nome: string; cor?: string }>>([]);
  const [selectedCardTypeIds, setSelectedCardTypeIds] = useState<string[]>([]);

  // Services management state
  const [entityServices, setEntityServices] = useState<any[]>([]);
  const [serviceTypes, setServiceTypes] = useState<any[]>([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [addServiceDialogOpen, setAddServiceDialogOpen] = useState(false);
  const [selectedServiceTypeId, setSelectedServiceTypeId] = useState('');
  const [selectedTarifarioId, setSelectedTarifarioId] = useState('');
  const [addingService, setAddingService] = useState(false);
  const [removingServiceId, setRemovingServiceId] = useState<string | null>(null);
  // Tarifário edit state
  const [availableTariffs, setAvailableTariffs] = useState<any[]>([]);
  const [tarifariosFalharam, setTarifariosFalharam] = useState(false);
  const [editTariffDialog, setEditTariffDialog] = useState(false);
  const [editingService, setEditingService] = useState<any | null>(null);
  const [editTarifarioId, setEditTarifarioId] = useState('');
  const [savingTariff, setSavingTariff] = useState(false);

  // Feedback
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  const fetchEntities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page + 1),
        limit: String(rowsPerPage),
        ...(searchQuery && { search: searchQuery }),
        ...(statusFilter && { status: statusFilter }),
        ...(typeFilter && { type: typeFilter }),
      });

      const response = await api.get(`/admin/entities?${params}`);
      if (response.data.success) {
        setEntities(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
        if (response.data.stats) {
          setStats(response.data.stats);
        }
      }
    } catch (error) {
      console.error('Error fetching entities:', error);
      setEntities([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, statusFilter, typeFilter]);

  const fetchEntityTypes = async () => {
    try {
      const response = await api.get('/admin/entities/types');
      if (response.data.success) {
        setEntityTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching entity types:', error);
      setEntityTypes([]);
    }
  };

  const fetchGeografia = async () => {
    try {
      const response = await api.get('/admin/entities/geografia');
      if (response.data.success) {
        setGeografiaList(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching geografia:', error);
      setGeografiaList([]);
    }
  };

  const fetchPosList = async () => {
    try {
      const response = await api.get('/admin/settings/pos-sisp');
      if (response.data.success) setPosList(response.data.data || []);
    } catch { /* ignore */ }
  };

  const fetchAllCardTypes = async () => {
    try {
      const response = await api.get('/admin/settings/card-types');
      if (response.data.success) setAllCardTypes(response.data.data || []);
    } catch { /* ignore */ }
  };

  const fetchEntityCardTypes = async (entityId: string) => {
    try {
      const response = await api.get(`/admin/settings/entities/${entityId}/card-types`);
      if (response.data.success) {
        setSelectedCardTypeIds((response.data.data || []).map((ct: any) => ct.tipoCartaoId));
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchEntities();
    fetchEntityTypes();
    fetchGeografia();
    fetchPosList();
    fetchAllCardTypes();
    // Load contact types on mount
    api.get('/admin/entities/contact-types').then((r) => {
      if (r.data?.success && r.data.data?.length > 0) setContactTypes(dedupeContactTypes(r.data.data));
    }).catch(() => {
      // Fallback to basic types if API fails
      setContactTypes([
        { id: '1', codigo: 'Email',     desig: 'Email' },
        { id: '2', codigo: 'Telefone',  desig: 'Telefone' },
        { id: '3', codigo: 'WhatsApp',  desig: 'WhatsApp' },
        { id: '4', codigo: 'Facebook',  desig: 'Facebook' },
        { id: '5', codigo: 'Instagram', desig: 'Instagram' },
        { id: '6', codigo: 'LinkedIn',  desig: 'LinkedIn' },
        { id: '7', codigo: 'Twitter',   desig: 'X' },
        { id: '8', codigo: 'YouTube',   desig: 'YouTube' },
        { id: '9', codigo: 'TikTok',    desig: 'TikTok' },
        { id: '10', codigo: 'Website',  desig: 'Website' },
      ]);
    });
  }, [fetchEntities]);

  useEffect(() => {
    if (!selectedEntity) return;
    if (viewTab === 2) fetchEntityServices(selectedEntity.id);
    if (viewTab === 6) fetchExtrato(selectedEntity.id, extratoPage);
    if (viewTab === 7) fetchLiquidacoes(selectedEntity.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewTab, selectedEntity?.id, extratoPage]);

  const fetchIntegrationData = async (entityId: string) => {
    setLoadingIntegration(true);
    try {
      const response = await api.get(`/admin/entities/${entityId}/integration`);
      if (response.data.success) {
        setIntegrationData(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching integration data:', error);
      setIntegrationData([]);
    } finally {
      setLoadingIntegration(false);
    }
  };

  const fetchWebhookConfig = async (entityId: string) => {
    setWebhookLoading(true);
    setWebhookSecretVisible(false);
    setWebhookRevealedSecret('');
    try {
      const res = await api.get(`/admin/entities/${entityId}/webhook`);
      if (res.data.success && res.data.data) {
        setWebhookConfig(res.data.data);
        setWebhookUrl(res.data.data.URL || '');
        setWebhookAtivo(!!res.data.data.ATIVO);
      } else {
        setWebhookConfig(null);
        setWebhookUrl('');
        setWebhookAtivo(true);
      }
      const dRes = await api.get(`/admin/entities/${entityId}/webhook/deliveries`);
      if (dRes.data.success) setWebhookDeliveries(dRes.data.data || []);
    } catch {
      setWebhookConfig(null);
    } finally {
      setWebhookLoading(false);
    }
  };

  const saveWebhookConfig = async () => {
    if (!selectedEntity) return;
    setWebhookSaving(true);
    try {
      await api.put(`/admin/entities/${selectedEntity.id}/webhook`, { url: webhookUrl, ativo: webhookAtivo });
      await fetchWebhookConfig(selectedEntity.id);
      setSuccessMsg('Webhook guardado');
    } catch (e: any) {
      setErrorMsg(e.response?.data?.error || 'Erro ao guardar webhook');
    } finally {
      setWebhookSaving(false);
    }
  };

  const regenerateWebhookSecret = async () => {
    if (!selectedEntity) return;
    if (!window.confirm('Gerar novo secret invalida o secret actual. Confirmar?')) return;
    setRegenerating(true);
    try {
      const res = await api.post(`/admin/entities/${selectedEntity.id}/webhook/regenerate`);
      if (res.data.success) {
        setWebhookRevealedSecret(res.data.secret);
        setWebhookSecretVisible(true);
        await fetchWebhookConfig(selectedEntity.id);
        setSuccessMsg('Novo secret gerado — copie agora, não será mostrado novamente');
      }
    } catch (e: any) {
      setErrorMsg(e.response?.data?.error || 'Erro ao regenerar secret');
    } finally {
      setRegenerating(false);
    }
  };

  const revealWebhookSecret = async () => {
    if (!selectedEntity) return;
    try {
      const res = await api.get(`/admin/entities/${selectedEntity.id}/webhook?reveal=1`);
      if (res.data.success && res.data.data?.SECRET) {
        setWebhookRevealedSecret(res.data.data.SECRET);
        setWebhookSecretVisible(true);
      }
    } catch { setErrorMsg('Erro ao revelar secret'); }
  };

  const fetchKnownUrls = async (anchor: HTMLButtonElement) => {
    if (!selectedEntity) return;
    setUrlPopoverAnchor(anchor);
    if (knownUrls.length > 0) return; // already loaded
    setLoadingKnownUrls(true);
    try {
      const res = await api.get(`/admin/entities/${selectedEntity.id}/webhook/known-urls`);
      if (res.data.success) setKnownUrls(res.data.data || []);
    } catch { setKnownUrls([]); }
    finally { setLoadingKnownUrls(false); }
  };

  const handleConsolidate = async () => {
    if (!selectedEntity) return;
    setConsolidating(true);
    try {
      const response = await api.post(`/admin/entities/${selectedEntity.id}/consolidate-payment-pages`);
      if (response.data.success) {
        setSuccessMsg(response.data.message || 'Páginas consolidadas com sucesso');
        setConsolidateDialogOpen(false);
        fetchIntegrationData(selectedEntity.id);
      }
    } catch (error: any) {
      const msg = error.response?.data?.error || error.response?.data?.message || error.message || 'Erro ao consolidar páginas';
      setErrorMsg(`Erro ao renomear: ${msg}`);
    } finally {
      setConsolidating(false);
    }
  };

  const handleMigratePaymentPages = async () => {
    if (!window.confirm(t('entities.confirmMigration'))) return;
    setMigratingPages(true);
    try {
      const response = await api.post('/admin/entities/migrate-payment-pages');
      if (response.data.success) {
        setSuccessMsg(response.data.message);
        fetchEntities();
      }
    } catch (error: any) {
      setErrorMsg(error.response?.data?.error || 'Erro na migração');
    } finally {
      setMigratingPages(false);
    }
  };

  const handleView = async (entity: EntityData) => {
    setViewDialogOpen(true);
    setViewTab(0);
    setLoadingDetails(true);

    try {
      const [response, typesResponse] = await Promise.all([
        api.get(`/admin/entities/${entity.id}`),
        contactTypes.length === 0 ? api.get('/admin/entities/contact-types') : Promise.resolve(null),
      ]);
      if (typesResponse?.data?.success) setContactTypes(dedupeContactTypes(typesResponse.data.data));
      if (response.data.success) {
        const data = response.data.data;
        setSelectedEntity(data);
        // Init SISP state from entity
        const hasSisp = !!(data.entidadeSisp && data.entidadeSisp !== '');
        setSispEnabled(hasSisp);
        setSispForm({
          entidadeSisp: data.entidadeSisp || '',
          tipoSisp: data.tipoSisp || 'Serviço',
          tokenSisp: data.tokenSisp || '',
        });
        // Init Pagali Pass plan
        setPlanoEventos(data.planoEventos || 'STARTER');
        // Load available plans (once)
        if (availablePlans.length === 0) {
          api.get('/admin/plans').then(r => {
            if (r.data?.data) setAvailablePlans(r.data.data.filter((p: any) => p.ativo));
          }).catch(() => {});
        }
        // Fetch SISP services
        if (hasSisp) {
          fetchSispServices(entity.id);
        } else {
          setSispServices([]);
        }
        // Fetch entity card types
        fetchEntityCardTypes(entity.id);
        // Fetch integration data
        fetchIntegrationData(entity.id);
        fetchWebhookConfig(entity.id);
      } else {
        setSelectedEntity(entity);
        setSispEnabled(false);
        setSispServices([]);
      }
    } catch (error) {
      console.error('Error fetching entity details:', error);
      setSelectedEntity(entity);
      setSispEnabled(false);
      setSispServices([]);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseView = () => {
    setViewDialogOpen(false);
    setSelectedEntity(null);
    setEditMode(false);
  };

  // Edit handlers
  const handleStartEdit = () => {
    if (!selectedEntity) return;
    setEditForm({
      desig: selectedEntity.desig || selectedEntity.name || '',
      nomeContacto: selectedEntity.nomeContacto || '',
      morada: selectedEntity.morada || selectedEntity.address || '',
      numConta: selectedEntity.numConta || '',
      banco: selectedEntity.banco || '',
      tipoId: selectedEntity.tipoId || '',
      posId: selectedEntity.posId || '',
      posAuthCode: selectedEntity.posAuthCode || '',
      posUrl: selectedEntity.posUrl || '',
      // Dados da Ficha de Adesão do contrato — antes só se definiam ao criar a entidade.
      nif: (selectedEntity as any).nif || '',
      nomeResp: (selectedEntity as any).nomeResp || '',
      emailEntidade: (selectedEntity as any).emailEntidade || '',
      telefone: (selectedEntity as any).telefone || '',
      website: (selectedEntity as any).website || '',
      geografiaId: (selectedEntity as any).geografiaId ? String((selectedEntity as any).geografiaId) : '',
      bancoId: (selectedEntity as any).bancoId || '',
    });
    if (!bancosList.length) {
      api.get('/admin/entities/bancos').then((r) => setBancosList(r.data?.data ?? [])).catch(() => {});
    }
    setEditMode(true);
    setViewTab(0);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
  };

  const handleSaveEdit = async () => {
    if (!selectedEntity) return;
    setSavingEdit(true);
    setErrorMsg('');
    try {
      await api.put(`/admin/entities/${selectedEntity.id}`, editForm);
      // Save card types
      await api.put(`/admin/settings/entities/${selectedEntity.id}/card-types`, { cardTypeIds: selectedCardTypeIds });
      setSuccessMsg(t('common.updatedSuccess'));
      setEditMode(false);
      fetchEntities();
      // Refresh details
      const response = await api.get(`/admin/entities/${selectedEntity.id}`);
      if (response.data.success) setSelectedEntity(response.data.data);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || t('common.errorSaving'));
    } finally {
      setSavingEdit(false);
    }
  };

  // Add contact
  const handleAddContact = async () => {
    if (!selectedEntity || !newContact.tipoCodigo || !newContact.contacto) return;
    setSavingContact(true);
    try {
      await api.post(`/admin/entities/${selectedEntity.id}/contacts`, newContact);
      const response = await api.get(`/admin/entities/${selectedEntity.id}`);
      if (response.data.success) setSelectedEntity(response.data.data);
      setNewContact({ tipoCodigo: '', contacto: '' });
      setAddContactOpen(false);
      setSuccessMsg('Contacto adicionado');
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || 'Erro ao adicionar contacto');
    } finally {
      setSavingContact(false);
    }
  };

  // Delete contact
  const handleDeleteContact = async (contactId: string) => {
    if (!selectedEntity) return;
    try {
      await api.delete(`/admin/entities/${selectedEntity.id}/contacts/${contactId}`);
      setSelectedEntity({ ...selectedEntity, contactos: selectedEntity.contactos?.filter((c: any) => c.id !== contactId) });
      setSuccessMsg('Contacto removido');
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || 'Erro ao remover contacto');
    }
  };

  // Status toggle
  const handleToggleStatus = async () => {
    if (!selectedEntity) return;
    const newEstado = selectedEntity.estado === 'A' ? 'I' : 'A';
    try {
      await api.put(`/admin/entities/${selectedEntity.id}/status`, { estado: newEstado });
      setSuccessMsg(t('common.updatedSuccess'));
      setSelectedEntity({ ...selectedEntity, estado: newEstado });
      fetchEntities();
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || t('common.errorSaving'));
    }
  };

  // Create handlers
  const handleOpenCreate = () => {
    setCreateForm({
      desig: '', nomeResp: '', nomeContacto: '', perfilCodigo: '', tipoId: '',
      nif: '', telefone: '', emailEntidade: '', website: '', descricao: '',
      morada: '', geografiaId: '',
      banco: '', numConta: '',
      taxaTransacao: '',
      email: '',
    });
    setSelectedUser(null);
    setUserSearchInput('');
    setUserSearchOptions([]);
    setCreateDialogOpen(true);
  };

  // Credentials dialog after entity creation
  const [credentialsDialog, setCredentialsDialog] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '', email: '' });

  // User search for entity creation
  const [selectedUser, setSelectedUser] = useState<{ id: string; nome: string; email: string; username: string } | null>(null);
  const [userSearchInput, setUserSearchInput] = useState('');
  const [userSearchOptions, setUserSearchOptions] = useState<any[]>([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);

  useEffect(() => {
    if (!userSearchInput || userSearchInput.length < 2) {
      setUserSearchOptions([]);
      return;
    }
    const timer = setTimeout(async () => {
      setUserSearchLoading(true);
      try {
        const res = await api.get(`/admin/entities/search-users?q=${encodeURIComponent(userSearchInput)}`);
        setUserSearchOptions(res.data.data || []);
      } catch {
        setUserSearchOptions([]);
      } finally {
        setUserSearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [userSearchInput]);

  const handleSaveCreate = async () => {
    if (!createForm.desig.trim()) {
      setErrorMsg(t('common.required'));
      return;
    }
    setSavingCreate(true);
    setErrorMsg('');
    try {
      const response = await api.post('/admin/entities', {
        ...createForm,
        existingUserId: selectedUser?.id || undefined,
      });
      const data = response.data.data;
      setCreateDialogOpen(false);
      fetchEntities();

      // Mostrar credenciais ao admin (apenas quando foi criada conta nova)
      if (data?.username && data?.password) {
        setCredentials({ username: data.username, password: data.password, email: data.email || '' });
        setCredentialsDialog(true);
      } else if (data?.existingUser) {
        setSuccessMsg('Entidade criada e associada à conta existente.');
      } else {
        setSuccessMsg(t('common.createdSuccess'));
      }
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || t('common.errorSaving'));
    } finally {
      setSavingCreate(false);
    }
  };

  // Logo upload handler
  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedEntity) return;

    if (!file.type.startsWith('image/')) {
      setErrorMsg(t('common.error'));
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setErrorMsg(t('common.error'));
      return;
    }

    setUploadingLogo(true);
    setErrorMsg('');
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64 = (reader.result as string).split(',')[1];
        await api.put(`/admin/entities/${selectedEntity.id}/logo`, {
          logo: base64,
          mimeType: file.type,
        });
        setSuccessMsg(t('common.updatedSuccess'));
        // Refresh entity details
        const response = await api.get(`/admin/entities/${selectedEntity.id}`);
        if (response.data.success) setSelectedEntity(response.data.data);
        setUploadingLogo(false);
      };
      reader.readAsDataURL(file);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || t('common.errorSaving'));
      setUploadingLogo(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedEntity) return;
    if (!window.confirm(`${t('entities.resetPassword')} "${selectedEntity.desig}"?`)) return;
    setResettingPassword(true);
    setErrorMsg('');
    try {
      const response = await api.post(`/admin/entities/${selectedEntity.id}/reset-password`);
      if (response.data.success) {
        setResetCredentials(response.data.data);
        setResetDialog(true);
      }
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || t('common.errorSaving'));
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCreateAccount = async () => {
    if (!selectedEntity) return;
    setCreatingAccount(true);
    setErrorMsg('');
    try {
      const response = await api.post(`/admin/entities/${selectedEntity.id}/create-account`);
      if (response.data.success) {
        setResetCredentials(response.data.data);
        setResetDialog(true);
        // Refresh entity to get new user info
        const refreshed = await api.get(`/admin/entities/${selectedEntity.id}`);
        if (refreshed.data.success) setSelectedEntity(refreshed.data.data);
        fetchEntities();
      }
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || t('common.errorSaving'));
    } finally {
      setCreatingAccount(false);
    }
  };

  const fetchSispServices = async (entityId: string) => {
    setLoadingSispServices(true);
    try {
      const response = await api.get(`/admin/entities/${entityId}/sisp-services`);
      if (response.data.success) {
        setSispServices(response.data.data || []);
      }
    } catch { /* ignore */ }
    finally { setLoadingSispServices(false); }
  };

  const handleSavePlano = async () => {
    if (!selectedEntity) return;
    setSavingPlano(true);
    setErrorMsg('');
    try {
      await api.put(`/admin/entities/${selectedEntity.id}`, { planoEventos });
      setSuccessMsg(`Plano de eventos actualizado para ${planoEventos}`);
      // Refresh entity data
      const response = await api.get(`/admin/entities/${selectedEntity.id}`);
      if (response.data.success) {
        setSelectedEntity(response.data.data);
        setPlanoEventos(response.data.data.planoEventos || 'STARTER');
      }
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || 'Erro ao guardar plano');
    } finally {
      setSavingPlano(false);
    }
  };

  const handleSyncPlanoEventos = async () => {
    if (!selectedEntity) return;
    setSavingPlano(true);
    setErrorMsg('');
    try {
      // First save the current plan
      await api.put(`/admin/entities/${selectedEntity.id}`, { planoEventos });
      // Then sync to all events of this entity
      const syncRes = await api.post('/admin/events/sync-plans', { entity_id: selectedEntity.id });
      const { updated = 0 } = syncRes.data;
      setSuccessMsg(`Plano ${planoEventos} aplicado à entidade e sincronizado em ${updated} evento(s).`);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || 'Erro ao sincronizar plano');
    } finally {
      setSavingPlano(false);
    }
  };

  const handleSaveSisp = async () => {
    if (!selectedEntity) return;
    setSavingSisp(true);
    setErrorMsg('');
    try {
      const payload = sispEnabled
        ? { entidadeSisp: sispForm.entidadeSisp, tipoSisp: sispForm.tipoSisp, tokenSisp: sispForm.tokenSisp || null }
        : { entidadeSisp: null, tipoSisp: null, tokenSisp: null };
      await api.put(`/admin/entities/${selectedEntity.id}`, payload);
      setSuccessMsg(sispEnabled ? t('entities.sispSaved') : t('entities.sispRemoved'));
      // Refresh entity
      const response = await api.get(`/admin/entities/${selectedEntity.id}`);
      if (response.data.success) {
        const data = response.data.data;
        setSelectedEntity(data);
        const hasSisp = !!(data.entidadeSisp && data.entidadeSisp !== '');
        setSispEnabled(hasSisp);
        setSispForm({ entidadeSisp: data.entidadeSisp || '', tipoSisp: data.tipoSisp || 'Serviço', tokenSisp: data.tokenSisp || '' });
        if (hasSisp) fetchSispServices(selectedEntity.id);
        else setSispServices([]);
      }
      fetchEntities();
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || err.message || t('common.errorSaving'));
    } finally {
      setSavingSisp(false);
    }
  };

  const fetchEntityServices = async (entityId: string) => {
    setLoadingServices(true);
    try {
      // O `catch` que aqui estava transformava qualquer avaria dos tarifários
      // numa lista vazia, e o ecrã ficava exactamente igual ao caso legítimo de
      // «não há tarifários». Quem estivesse a usar não tinha como distinguir os
      // dois. Agora o erro é registado e assinalado; a lista vazia continua a
      // não bloquear o resto, porque o tarifário é opcional.
      const [svcRes, typesRes, tarRes] = await Promise.all([
        api.get(`/admin/entities/${entityId}/services`),
        api.get(`/admin/entities/service-types`),
        api.get(`/admin/invoices/tariffs`).catch((e) => {
          console.error('[entities] falha a carregar tarifários:', e?.message || e);
          return { data: { data: [], _falhou: true } };
        }),
      ]);
      if (svcRes.data.success) setEntityServices(svcRes.data.data || []);
      if (typesRes.data.success) setServiceTypes(typesRes.data.data || []);
      setAvailableTariffs(tarRes.data.data || []);
      setTarifariosFalharam(Boolean((tarRes.data as any)._falhou));
    } catch (e: any) {
      console.error('[entities] falha a carregar serviços da entidade:', e?.message || e);
    } finally {
      setLoadingServices(false);
    }
  };

  const handleAddService = async () => {
    if (!selectedEntity?.id || !selectedServiceTypeId) return;
    setAddingService(true);
    try {
      const res = await api.post(`/admin/entities/${selectedEntity.id}/services`, {
        tipoServicoId: selectedServiceTypeId,
        tarifarioId: selectedTarifarioId || undefined,
      });
      if (res.data.success) {
        setSuccessMsg(res.data.message);
        setAddServiceDialogOpen(false);
        setSelectedServiceTypeId('');
        setSelectedTarifarioId('');
        fetchEntityServices(selectedEntity.id);
      }
    } catch (err: any) {
      setErrorMsg(err.response?.data?.error || 'Erro ao adicionar serviço');
    } finally {
      setAddingService(false);
    }
  };

  // Lista «Todos os Serviços» da app: estes tipos não aparecem, salvo indicação em contrário
  // (mesma regra que utils/visibilidade-app.ts na API).
  const TIPOS_FORA_DA_APP = ['ECM', 'EVT', 'SHOP', 'DNT'];
  const [savingAppId, setSavingAppId] = useState<string | null>(null);
  const handleVisivelApp = async (serv: any, valor: '' | 'S' | 'N') => {
    if (!selectedEntity) return;
    setSavingAppId(serv.esId);
    try {
      await api.put(`/admin/entities/${selectedEntity.id}/services/${serv.esId}`, { visivelApp: valor || null });
      setEntityServices((prev) => prev.map((x) => (x.esId === serv.esId ? { ...x, visivelApp: valor || null } : x)));
    } catch (e: any) {
      alert(e?.response?.data?.error || 'Não foi possível gravar');
    } finally {
      setSavingAppId(null);
    }
  };

  const handleRemoveService = async (esId: string) => {
    if (!selectedEntity?.id) return;
    setRemovingServiceId(esId);
    try {
      const res = await api.delete(`/admin/entities/${selectedEntity.id}/services/${esId}`);
      if (res.data.success) {
        setSuccessMsg(res.data.message);
        fetchEntityServices(selectedEntity.id);
      }
    } catch (err: any) {
      setErrorMsg(err.response?.data?.error || 'Erro ao remover serviço');
    } finally {
      setRemovingServiceId(null);
    }
  };

  const handleOpenEditTariff = (serv: any) => {
    setEditingService(serv);
    setEditTarifarioId(serv.tarifarioId || '');
    setEditTariffDialog(true);
  };

  const handleSaveTariff = async () => {
    if (!selectedEntity?.id || !editingService) return;
    setSavingTariff(true);
    try {
      const res = await api.put(
        `/admin/entities/${selectedEntity.id}/services/${editingService.esId}`,
        { tarifarioId: editTarifarioId || null }
      );
      if (res.data.success) {
        // A tarifa grava-se mesmo que a comissão da loja não acompanhe; nesse caso
        // diz-se porquê, em vez de dar o «atualizado» que escondia a falha.
        if (res.data.aviso) setErrorMsg(res.data.aviso);
        else setSuccessMsg(res.data.loja?.accao === 'CRIADA'
          ? `Tarifário atualizado — subscrição da loja criada a ${res.data.loja.percentagem}%`
          : 'Tarifário atualizado');
        setEditTariffDialog(false);
        fetchEntityServices(selectedEntity.id);
      }
    } catch (err: any) {
      setErrorMsg(err.response?.data?.error || 'Erro ao atualizar tarifário');
    } finally {
      setSavingTariff(false);
    }
  };

  const fetchExtrato = async (entityId: string, page = 0) => {
    setExtratoLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page + 1), limit: '15' });
      const response = await api.get(`/admin/entities/${entityId}/extrato?${params}`);
      if (response.data.success) {
        setExtratoData(response.data.data || []);
        setExtratoStats(response.data.stats || { totalTransacoes: 0, totalBruto: 0, totalComissao: 0, totalIva: 0, totalLiquido: 0 });
        setExtratoTotal(response.data.pagination?.total || 0);
      }
    } catch { /* ignore */ }
    finally { setExtratoLoading(false); }
  };

  const fetchLiquidacoes = async (entityId: string) => {
    setLiquidacoesLoading(true);
    try {
      const response = await api.get(`/admin/entities/${entityId}/liquidacoes?limit=50`);
      if (response.data.success) {
        setLiquidacoesData(response.data.data || []);
        setLiquidacoesStats(response.data.stats || { total: 0, totalLiquidado: 0, totalPendente: 0 });
      }
    } catch { /* ignore */ }
    finally { setLiquidacoesLoading(false); }
  };

  const handleSaveLiquidacao = async () => {
    if (!selectedEntity) return;
    setSavingLiquidacao(true);
    try {
      await api.post(`/admin/entities/${selectedEntity.id}/liquidacoes`, {
        ...liquidacaoForm,
        montante: parseFloat(liquidacaoForm.montante),
      });
      setSuccessMsg(t('entities.liquidacaoCreated'));
      setLiquidacaoFormOpen(false);
      setLiquidacaoForm({ montante: '', dtLiquidacao: '', dtPeriodoIni: '', dtPeriodoFim: '', referencia: '', notas: '' });
      fetchLiquidacoes(selectedEntity.id);
    } catch (err: any) {
      setErrorMsg(err?.response?.data?.error || t('common.errorSaving'));
    } finally {
      setSavingLiquidacao(false);
    }
  };

  const handleUpdateLiquidacaoEstado = async (lid: string, estado: string) => {
    if (!selectedEntity) return;
    try {
      await api.put(`/admin/entities/${selectedEntity.id}/liquidacoes/${lid}`, { estado });
      fetchLiquidacoes(selectedEntity.id);
    } catch { /* ignore */ }
  };

  const columns: Column<EntityData>[] = [
    {
      id: 'desig',
      label: t('common.entity'),
      minWidth: 220,
      format: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: row?.estado === 'A' ? 'primary.main' : 'grey.400', width: 40, height: 40 }}>
            <Business />
          </Avatar>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2" fontWeight={500}>
                {value}
              </Typography>
              {(row as any)?.entidadeSisp && (
                <Chip label="SISP" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: 10, fontWeight: 700 }} />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {t('common.code')}: {row?.codigo}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'tipo',
      label: t('common.type'),
      minWidth: 120,
    },
    {
      id: 'banco',
      label: t('entities.bank'),
      minWidth: 100,
      format: (value) => value || '-',
    },
    {
      id: 'telefone',
      label: t('common.phone'),
      minWidth: 100,
      format: (value) => value || '-',
    },
    {
      id: 'servicos',
      label: t('entities.services'),
      minWidth: 80,
      align: 'center',
      format: (value) => (
        <Chip label={value || 0} size="small" color="primary" variant="outlined" />
      ),
    },
    {
      id: 'dataRegisto',
      label: t('common.dateRegister'),
      minWidth: 110,
      format: (value) => <DateCell value={value} />,
    },
    {
      id: 'estado',
      label: t('common.status'),
      minWidth: 100,
      align: 'center',
      format: (value) => (
        <StatusChip
          status={value === 'A' ? t('common.active') : t('common.inactive')}
          colorMap={{
            [t('common.active')]: 'success',
            A: 'success',
            [t('common.inactive')]: 'default',
            I: 'default',
          }}
        />
      ),
    },
  ];

  const formatCurrency = (value: number) =>
    `${Math.round(value || 0).toLocaleString('pt-PT')}$`;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('entities.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('entities.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="outlined" startIcon={<Add />} onClick={handleOpenCreate}>
            {t('entities.newEntity')}
          </Button>
          <Button variant="outlined" color="secondary" startIcon={<Payment />} onClick={handleMigratePaymentPages} disabled={migratingPages}>
            {migratingPages ? t('common.loading') : t('entities.migratePaymentPages')}
          </Button>
          <Button variant="contained" startIcon={<Refresh />} onClick={fetchEntities}>
            {t('common.refresh')}
          </Button>
        </Box>
      </Box>

      {/* Feedback Alerts — rendered via Snackbar so they appear over dialogs */}
      <Snackbar
        open={!!successMsg}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccessMsg('')} sx={{ width: '100%' }}>{successMsg}</Alert>
      </Snackbar>
      <Snackbar
        open={!!errorMsg}
        autoHideDuration={6000}
        onClose={() => setErrorMsg('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMsg('')} sx={{ width: '100%' }}>{errorMsg}</Alert>
      </Snackbar>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'primary.light', color: 'primary.main' }}>
                  <Business />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.total}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('entities.totalEntities')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'success.light', color: 'success.main' }}>
                  <CheckCircle />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.ativos}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('entities.active')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.selected', color: 'text.secondary' }}>
                  <Cancel />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.inativos}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('entities.inactive')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'info.light', color: 'info.main' }}>
                  <Store />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>{stats.servicos}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('entities.totalServices')}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label={t('common.search')}
              placeholder={t('entities.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('common.status')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.allFemale')}</MenuItem>
              <MenuItem value="A">{t('entities.active')}</MenuItem>
              <MenuItem value="I">{t('entities.inactive')}</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('common.type')}
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {entityTypes.map((type) => (
                <MenuItem key={type.id} value={type.id}>
                  {type.nome}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={entities}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(rpp) => {
          setRowsPerPage(rpp);
          setPage(0);
        }}
        onRefresh={fetchEntities}
        onView={handleView}
        rowKey="id"
        emptyMessage={t('entities.noEntities')}
      />

      {/* View Entity Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Logo with upload */}
            <Box sx={{ position: 'relative' }}>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                id="entity-logo-upload"
                style={{ display: 'none' }}
                onChange={handleLogoUpload}
              />
              <label htmlFor="entity-logo-upload" style={{ cursor: 'pointer' }}>
                {(() => {
                  const logoBase64 = bufferToBase64(selectedEntity?.logo);
                  return logoBase64 ? (
                    <Avatar
                      src={`data:${selectedEntity?.mimeType || 'image/png'};base64,${logoBase64}`}
                      sx={{ width: 56, height: 56, border: '2px solid', borderColor: 'primary.light' }}
                    />
                  ) : (
                    <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56 }}>
                      <Business />
                    </Avatar>
                  );
                })()}
                {uploadingLogo && (
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex',
                    alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.5)', borderRadius: '50%' }}>
                    <Typography variant="caption" color="white">...</Typography>
                  </Box>
                )}
              </label>
            </Box>
            <Box>
              <Typography variant="h6">{selectedEntity?.desig || selectedEntity?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('common.code')}: {selectedEntity?.codigo} · <em style={{ fontSize: 11 }}>{t('entities.logoClick')}</em>
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseView}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Tabs value={viewTab} onChange={(_, v) => setViewTab(v)} variant="scrollable" scrollButtons="auto">
            <Tab label={t('entities.generalData')} />
            <Tab label={t('entities.contacts')} />
            <Tab label={t('entities.services')} />
            <Tab label={t('entities.sisp')} icon={<CreditCard />} iconPosition="start" />
            <Tab label={t('entities.statistics')} />
            <Tab label={t('entities.integration')} icon={<LinkIcon />} iconPosition="start" />
            <Tab label={t('entities.extrato')} icon={<SwapHoriz />} iconPosition="start" />
            <Tab label={t('entities.liquidacoes')} icon={<AccountBalanceWallet />} iconPosition="start" />
          </Tabs>

          {loadingDetails ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">{t('common.loading')}</Typography>
            </Box>
          ) : (
            <>
              <TabPanel value={viewTab} index={0}>
                {editMode ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label={t('entities.designation')} value={editForm.desig}
                        onChange={(e) => setEditForm({ ...editForm, desig: e.target.value })} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" select label={t('common.type')} value={editForm.tipoId}
                        onChange={(e) => setEditForm({ ...editForm, tipoId: e.target.value })}>
                        <MenuItem value="">-</MenuItem>
                        {entityTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      {/* Lista e não texto: a API grava o BANCO_ID; o texto livre nunca ficava gravado. */}
                      <TextField fullWidth size="small" select label={t('entities.bank')} value={editForm.bancoId}
                        onChange={(e) => setEditForm({ ...editForm, bancoId: e.target.value })}>
                        <MenuItem value="">-</MenuItem>
                        {bancosList.map((b) => <MenuItem key={b.id} value={b.id}>{b.nome}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label={t('entities.bankAccount')} value={editForm.numConta}
                        onChange={(e) => setEditForm({ ...editForm, numConta: e.target.value })} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label={t('entities.contact')} value={editForm.nomeContacto}
                        onChange={(e) => setEditForm({ ...editForm, nomeContacto: e.target.value })} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label={t('common.address')} value={editForm.morada}
                        onChange={(e) => setEditForm({ ...editForm, morada: e.target.value })} />
                    </Grid>

                    {/* Dados da Ficha de Adesão do contrato (aparecem ao promotor no events.cv). */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>Ficha de Adesão do contrato</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label="NIF" value={editForm.nif}
                        onChange={(e) => setEditForm({ ...editForm, nif: e.target.value })} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" select label="Concelho / Ilha" value={editForm.geografiaId}
                        onChange={(e) => setEditForm({ ...editForm, geografiaId: e.target.value })}>
                        <MenuItem value="">-</MenuItem>
                        {geografiaList.map((g) => <MenuItem key={g.id} value={String(g.id)}>{g.concelho} — {g.ilha}</MenuItem>)}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label="Responsável" value={editForm.nomeResp}
                        onChange={(e) => setEditForm({ ...editForm, nomeResp: e.target.value })} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label="Email da entidade" value={editForm.emailEntidade}
                        onChange={(e) => setEditForm({ ...editForm, emailEntidade: e.target.value })} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label="Telefone" value={editForm.telefone}
                        onChange={(e) => setEditForm({ ...editForm, telefone: e.target.value })} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField fullWidth size="small" label="Website" value={editForm.website}
                        onChange={(e) => setEditForm({ ...editForm, website: e.target.value })} />
                    </Grid>

                    {/* Gateway SISP */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>{t('entities.sispConfig')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <TextField fullWidth size="small" select label={t('entities.selectPos')}
                        value={posList.find(p => p.pos === editForm.posId && p.posUrl === editForm.posUrl)?.id || ''}
                        onChange={(e) => {
                          const selected = posList.find(p => p.id === e.target.value);
                          if (selected) {
                            setEditForm({ ...editForm, posId: selected.pos, posAuthCode: selected.posAuthCode, posUrl: selected.posUrl });
                          } else {
                            setEditForm({ ...editForm, posId: '', posAuthCode: '', posUrl: '' });
                          }
                        }}>
                        <MenuItem value="">- {t('entities.selectPos')} -</MenuItem>
                        {posList.map((p) => (
                          <MenuItem key={p.id} value={p.id}>
                            {p.designation} ({p.pos}) - {p.environment} · {p.servico || t('settings.posSisp.allServices')}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField fullWidth size="small" label={t('entities.posId')} value={editForm.posId}
                        onChange={(e) => setEditForm({ ...editForm, posId: e.target.value })}
                        slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField fullWidth size="small" label={t('entities.posAuthCode')} value={editForm.posAuthCode}
                        onChange={(e) => setEditForm({ ...editForm, posAuthCode: e.target.value })}
                        slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }} />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField fullWidth size="small" label={t('entities.posUrl')} value={editForm.posUrl}
                        onChange={(e) => setEditForm({ ...editForm, posUrl: e.target.value })} />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('entities.designation')}</Typography>
                      <Typography variant="body1">{selectedEntity?.desig || selectedEntity?.name}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('common.type')}</Typography>
                      <Typography variant="body1">{selectedEntity?.tipo || selectedEntity?.typeName || '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('entities.bank')}</Typography>
                      <Typography variant="body1">{selectedEntity?.banco || '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('entities.bankAccount')}</Typography>
                      <Typography variant="body1">{selectedEntity?.numConta || '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('entities.contact')}</Typography>
                      <Typography variant="body1">{selectedEntity?.nomeContacto || '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('common.address')}</Typography>
                      <Typography variant="body1">{selectedEntity?.morada || selectedEntity?.address || '-'}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('common.dateRegister')}</Typography>
                      <Typography variant="body1">
                        {selectedEntity?.dataRegisto ? new Date(selectedEntity.dataRegisto).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <Typography variant="caption" color="text.secondary">{t('common.status')}</Typography>
                      <Box sx={{ mt: 0.5 }}>
                        <Chip
                          label={selectedEntity?.estado === 'A' ? t('common.active') : t('common.inactive')}
                          color={selectedEntity?.estado === 'A' ? 'success' : 'default'}
                          size="small"
                        />
                      </Box>
                    </Grid>

                    {/* Gateway SISP (view mode) */}
                    {(selectedEntity?.posId || selectedEntity?.posUrl) && (
                      <>
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="subtitle2" sx={{ mt: 2, color: 'text.secondary' }}>{t('entities.gatewaySisp')}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">{t('entities.posId')}</Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{selectedEntity?.posId || '-'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">{t('entities.posAuthCode')}</Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{selectedEntity?.posAuthCode ? '••••••••' : '-'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">{t('entities.posUrl')}</Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{selectedEntity?.posUrl || '-'}</Typography>
                        </Grid>
                      </>
                    )}

                    {/* Tipos de Cartão Aceites */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>{t('common.cardTypes')}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      {editMode ? (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {allCardTypes.map((ct) => {
                            const isSelected = selectedCardTypeIds.includes(ct.id);
                            return (
                              <Chip
                                key={ct.id}
                                label={ct.nome}
                                onClick={() => {
                                  setSelectedCardTypeIds(isSelected
                                    ? selectedCardTypeIds.filter(id => id !== ct.id)
                                    : [...selectedCardTypeIds, ct.id]
                                  );
                                }}
                                color={isSelected ? 'primary' : 'default'}
                                variant={isSelected ? 'filled' : 'outlined'}
                                sx={{
                                  borderColor: ct.cor || undefined,
                                  ...(isSelected && ct.cor ? { bgcolor: ct.cor, '&:hover': { bgcolor: ct.cor, opacity: 0.9 } } : {}),
                                }}
                              />
                            );
                          })}
                          {allCardTypes.length === 0 && (
                            <Typography variant="caption" color="text.secondary">{t('common.noData')}</Typography>
                          )}
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {selectedEntity?.tiposCartao?.length > 0 ? (
                            selectedEntity.tiposCartao.map((ct: any) => (
                              <Chip
                                key={ct.id}
                                label={ct.nome}
                                size="small"
                                sx={{ bgcolor: ct.cor || undefined, color: ct.cor ? '#fff' : undefined }}
                              />
                            ))
                          ) : (
                            <Typography variant="caption" color="text.secondary">{t('common.noData')}</Typography>
                          )}
                        </Box>
                      )}
                    </Grid>

                    {/* Conta Comerciante */}
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, color: 'text.secondary' }}>{t('entities.merchantAccount')}</Typography>
                    </Grid>
                    {selectedEntity?.username ? (
                      <>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">{t('common.username')}</Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>{selectedEntity.username}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">{t('common.email')}</Typography>
                          <Typography variant="body1">{selectedEntity.userEmail || '-'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4 }}>
                          <Typography variant="caption" color="text.secondary">{t('entities.accountStatus')}</Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={selectedEntity.userEstado === 'A' ? t('common.active') : t('common.inactive')}
                              color={selectedEntity.userEstado === 'A' ? 'success' : 'default'}
                              size="small"
                            />
                          </Box>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            color="warning"
                            startIcon={<VpnKey />}
                            onClick={handleResetPassword}
                            disabled={resettingPassword}
                            sx={{ mt: 1 }}
                          >
                            {resettingPassword ? t('entities.resettingPassword') : t('entities.resetPassword')}
                          </Button>
                        </Grid>
                      </>
                    ) : (
                      <Grid size={{ xs: 12 }}>
                        <Alert severity="warning" variant="outlined" sx={{ fontSize: 13 }}>
                          {t('entities.noMerchantAccount')} {t('entities.createMerchantAlert')}
                        </Alert>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Add />}
                          onClick={handleCreateAccount}
                          disabled={creatingAccount}
                          sx={{ mt: 1 }}
                        >
                          {creatingAccount ? t('entities.creatingAccount') : t('entities.createAccount')}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                )}
              </TabPanel>

              <TabPanel value={viewTab} index={1}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Contactos e Redes Sociais</Typography>
                  <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => setAddContactOpen(true)}>
                    Adicionar
                  </Button>
                </Box>
                <List disablePadding>
                  {selectedEntity?.contactos?.length > 0 ? (
                    selectedEntity.contactos.map((contacto: any, index: number) => (
                      <ListItem key={contacto.id || index} divider
                        secondaryAction={
                          <IconButton size="small" edge="end" onClick={() => handleDeleteContact(contacto.id)} color="error">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {contacto.tipoCodigo === 'Email' ? <Email color="primary" fontSize="small" /> :
                           contacto.tipoCodigo === 'Telefone' || contacto.tipoCodigo === 'TLM' ? <Phone color="primary" fontSize="small" /> :
                           contacto.tipoCodigo === 'Website' ? <LinkIcon color="primary" fontSize="small" /> :
                           <ContactMail color="primary" fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText
                          primary={contacto.contacto}
                          secondary={contacto.tipo || contacto.tipoCodigo}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))
                  ) : (
                    <Typography color="text.secondary" sx={{ py: 2, px: 1 }}>
                      Sem contactos registados. Clique em Adicionar.
                    </Typography>
                  )}
                </List>

                {/* Add Contact Dialog */}
                <Dialog open={addContactOpen} onClose={() => setAddContactOpen(false)} maxWidth="xs" fullWidth>
                  <DialogTitle>Adicionar Contacto</DialogTitle>
                  <DialogContent sx={{ pt: '16px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      select fullWidth size="small" label="Tipo"
                      value={newContact.tipoCodigo}
                      onChange={(e) => setNewContact({ ...newContact, tipoCodigo: e.target.value })}
                    >
                      {contactTypes.map((t) => (
                        <MenuItem key={t.id} value={t.codigo}>{t.desig}</MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      fullWidth size="small" label="Valor"
                      placeholder={
                        newContact.tipoCodigo === 'Email' ? 'email@exemplo.com' :
                        newContact.tipoCodigo === 'Telefone' ? '+238 XXX XXXX' :
                        newContact.tipoCodigo === 'Website' ? 'https://...' :
                        newContact.tipoCodigo === 'Facebook' ? 'https://facebook.com/pagina' :
                        newContact.tipoCodigo === 'Instagram' ? 'https://instagram.com/utilizador' :
                        newContact.tipoCodigo === 'LinkedIn' ? 'https://linkedin.com/company/...' :
                        newContact.tipoCodigo === 'WhatsApp' ? '+238 XXX XXXX' : 'URL ou valor'
                      }
                      value={newContact.contacto}
                      onChange={(e) => setNewContact({ ...newContact, contacto: e.target.value })}
                    />
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setAddContactOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleAddContact} disabled={savingContact || !newContact.tipoCodigo || !newContact.contacto}>
                      {savingContact ? <CircularProgress size={18} /> : 'Guardar'}
                    </Button>
                  </DialogActions>
                </Dialog>
              </TabPanel>

              <TabPanel value={viewTab} index={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {t('entities.servicesManageHint', 'Serviços disponíveis no portal desta entidade')}
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => { setSelectedServiceTypeId(''); setAddServiceDialogOpen(true); }}
                  >
                    {t('entities.addService', 'Adicionar Serviço')}
                  </Button>
                </Box>
                {loadingServices ? (
                  <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
                ) : entityServices.length > 0 ? (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('common.service')}</TableCell>
                        <TableCell>{t('common.type', 'Tipo')}</TableCell>
                        <TableCell>Cobra</TableCell>
                        <TableCell title="Aparece na lista «Todos os Serviços» da app Pagali">App</TableCell>
                        <TableCell align="center">{t('common.status')}</TableCell>
                        <TableCell align="center">{t('common.actions', 'Ações')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {entityServices.map((serv: any) => (
                        <TableRow key={serv.esId}>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>{serv.nome}</Typography>
                            <Typography variant="caption" color="text.secondary">{serv.descricao || ''}</Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={serv.tipoCodigo || serv.tipo || '-'} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <CobrancaCell serv={serv} />
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const porTipo = !TIPOS_FORA_DA_APP.includes(String(serv.tipoCodigo || '').toUpperCase());
                              return (
                                <TextField
                                  select
                                  size="small"
                                  variant="standard"
                                  value={serv.visivelApp || ''}
                                  disabled={savingAppId === serv.esId}
                                  onChange={(e) => handleVisivelApp(serv, e.target.value as '' | 'S' | 'N')}
                                  sx={{ minWidth: 150 }}
                                >
                                  <MenuItem value="">Automático ({porTipo ? 'mostra' : 'esconde'})</MenuItem>
                                  <MenuItem value="S">Mostrar sempre</MenuItem>
                                  <MenuItem value="N">Esconder sempre</MenuItem>
                                </TextField>
                              );
                            })()}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={serv.estado === 'A' ? t('common.active') : t('common.inactive')}
                              color={serv.estado === 'A' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="center">
                            {serv.cobranca?.servico && (
                              <IconButton
                                size="small"
                                color={serv.cobranca.contrato ? 'success' : 'default'}
                                title={serv.cobranca.contrato || serv.cobranca.rascunho ? 'Abrir contrato' : 'Criar contrato'}
                                onClick={() => navigate(
                                  `/contratos?entidade=${encodeURIComponent(selectedEntity?.id || '')}`
                                  + `&servico=${encodeURIComponent(serv.cobranca.servico)}`)}
                              >
                                <ContratoIcon fontSize="small" />
                              </IconButton>
                            )}
                            <IconButton
                              size="small"
                              title="Alterar tarifário"
                              onClick={() => handleOpenEditTariff(serv)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              title={t('entities.removeService', 'Remover do portal')}
                              disabled={removingServiceId === serv.esId}
                              onClick={() => handleRemoveService(serv.esId)}
                            >
                              {removingServiceId === serv.esId ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Typography color="text.secondary" sx={{ py: 2 }}>
                    {t('entities.noServices')}
                  </Typography>
                )}
              </TabPanel>

              <TabPanel value={viewTab} index={3}>
                {/* Tipo de integração */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, p: 2, bgcolor: sispEnabled ? 'info.light' : 'action.hover', borderRadius: 1, border: '1px solid', borderColor: sispEnabled ? 'info.main' : 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <CreditCard color={sispEnabled ? 'info' : 'disabled'} />
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {sispEnabled ? t('entities.sispEntity') : t('entities.pagaliEntity')}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {sispEnabled ? t('entities.sispActive') : t('entities.sispInactive')}
                      </Typography>
                    </Box>
                  </Box>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sispEnabled}
                        onChange={(e) => setSispEnabled(e.target.checked)}
                        color="primary"
                      />
                    }
                    label={sispEnabled ? t('entities.sisp') : 'Pagali'}
                    labelPlacement="start"
                  />
                </Box>

                {/* Campos SISP */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('entities.sispCode')}
                      value={sispForm.entidadeSisp}
                      onChange={(e) => setSispForm({ ...sispForm, entidadeSisp: e.target.value })}
                      helperText={t('entities.sispCodeHelper')}
                      disabled={!sispEnabled}
                      slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      select
                      label={t('entities.sispType')}
                      value={sispForm.tipoSisp}
                      onChange={(e) => setSispForm({ ...sispForm, tipoSisp: e.target.value })}
                      helperText={t('entities.sispTypeHelper')}
                      disabled={!sispEnabled}
                    >
                      <MenuItem value="Compra">1 - Compra</MenuItem>
                      <MenuItem value="Serviço">2 - Pagamento de Servico</MenuItem>
                      <MenuItem value="Recarga">3 - Recarga / Pre-pago / Doacao</MenuItem>
                      <MenuItem value="Estorno">4 - Estorno</MenuItem>
                      <MenuItem value="Token Requisicao">5 - Requisicao de Token</MenuItem>
                      <MenuItem value="Token Pagamento">6 - Pagamento com Token</MenuItem>
                      <MenuItem value="Token Cancelar">7 - Cancelar Token</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('entities.sispToken')}
                      value={sispForm.tokenSisp}
                      onChange={(e) => setSispForm({ ...sispForm, tokenSisp: e.target.value })}
                      helperText={t('entities.sispTokenHelper')}
                      disabled={!sispEnabled}
                      slotProps={{ htmlInput: { style: { fontFamily: 'monospace' } } }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<Save />}
                    onClick={handleSaveSisp}
                    disabled={savingSisp || (sispEnabled && !sispForm.entidadeSisp)}
                  >
                    {savingSisp ? t('common.saving') : t('entities.saveSisp')}
                  </Button>
                </Box>

                {/* Serviços SISP associados */}
                {sispEnabled && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
                      {t('entities.sispServices')}
                    </Typography>
                    {loadingSispServices ? (
                      <Typography variant="body2" color="text.secondary">{t('common.loading')}</Typography>
                    ) : sispServices.length > 0 ? (
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('common.service')}</TableCell>
                              <TableCell>{t('entities.sispCode')}</TableCell>
                              <TableCell align="center">{t('common.status')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {sispServices.map((ss: any) => (
                              <TableRow key={ss.id}>
                                <TableCell>{ss.serviceName}</TableCell>
                                <TableCell>
                                  <Chip label={ss.sispService} size="small" variant="outlined" sx={{ fontFamily: 'monospace' }} />
                                </TableCell>
                                <TableCell align="center">
                                  <Chip
                                    label={ss.active === 'S' ? t('common.active') : t('common.inactive')}
                                    color={ss.active === 'S' ? 'success' : 'default'}
                                    size="small"
                                  />
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        {t('entities.noSispServices')}
                      </Typography>
                    )}
                  </>
                )}

                {/* ── Pagali Pass — Plano de Eventos ── */}
                <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Pagali Pass — Plano de Eventos
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    O plano define as funcionalidades disponíveis para todos os eventos desta entidade (herança automática ao criar evento).
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                    {availablePlans.length === 0 ? (
                      <TextField
                        size="small"
                        label="Código do Plano"
                        value={planoEventos}
                        onChange={e => setPlanoEventos(e.target.value.toUpperCase())}
                        sx={{ minWidth: 160 }}
                        slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontWeight: 700 } } }}
                      />
                    ) : (
                      availablePlans.map(p => (
                        <Card
                          key={p.codigo}
                          variant="outlined"
                          onClick={() => setPlanoEventos(p.codigo)}
                          sx={{
                            cursor: 'pointer',
                            minWidth: 120,
                            borderColor: planoEventos === p.codigo ? 'primary.main' : 'divider',
                            borderWidth: planoEventos === p.codigo ? 2 : 1,
                            bgcolor: planoEventos === p.codigo ? 'primary.50' : 'background.paper',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: 'primary.light', bgcolor: 'action.hover' },
                          }}
                        >
                          <CardContent sx={{ py: '10px !important', px: 1.5, textAlign: 'center' }}>
                            {p.destaque && (
                              <Chip label="Popular" size="small" color="warning" sx={{ mb: 0.5, height: 16, fontSize: 9 }} />
                            )}
                            <Typography variant="subtitle2" fontWeight={700} color={planoEventos === p.codigo ? 'primary.main' : 'text.primary'}>
                              {p.nome}
                            </Typography>
                            {p.descricao && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                {p.descricao.length > 40 ? p.descricao.substring(0, 40) + '…' : p.descricao}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<Save />}
                      onClick={handleSavePlano}
                      disabled={savingPlano}
                    >
                      {savingPlano ? 'A guardar…' : 'Guardar Plano'}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={handleSyncPlanoEventos}
                      disabled={savingPlano}
                      title="Guarda o plano na entidade e aplica-o a todos os eventos existentes desta entidade"
                    >
                      {savingPlano ? 'A sincronizar…' : 'Guardar e Sincronizar Eventos'}
                    </Button>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel value={viewTab} index={4}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Payment color="primary" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h5" fontWeight={700}>
                          {selectedEntity?.stats?.totalTransactions || selectedEntity?.stats?.payments || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('entities.transactions')}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <AccountBalance color="success" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h5" fontWeight={700}>
                          {formatCurrency(selectedEntity?.stats?.totalRevenue || selectedEntity?.stats?.amount || 0)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('entities.totalVolume')}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <Card variant="outlined">
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Store color="info" sx={{ fontSize: 40, mb: 1 }} />
                        <Typography variant="h5" fontWeight={700}>
                          {selectedEntity?.stats?.totalServices || (selectedEntity?.servicos?.length) || (selectedEntity?.services?.length) || 0}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t('entities.services')}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Integration Tab */}
              <TabPanel value={viewTab} index={5}>
                <Typography variant="h6" gutterBottom>{t('entities.integrationTitle')}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {t('entities.integrationDescription')}
                </Typography>

                {integrationData.length >= 1 && (
                  <Alert
                    severity={integrationData.length > 1 ? 'warning' : 'info'}
                    sx={{ mb: 2 }}
                    action={
                      <Button
                        size="small"
                        color={integrationData.length > 1 ? 'warning' : 'info'}
                        variant="contained"
                        startIcon={<CallMerge />}
                        onClick={() => setConsolidateDialogOpen(true)}
                      >
                        {integrationData.length > 1 ? 'Consolidar em uma' : 'Renomear para entidade'}
                      </Button>
                    }
                  >
                    {integrationData.length > 1
                      ? `Esta entidade tem ${integrationData.length} páginas de pagamento activas. Consolida em uma só.`
                      : `Renomear a página de pagamento para "${selectedEntity?.desig}".`}
                  </Alert>
                )}

                {loadingIntegration ? (
                  <Typography>{t('common.loading')}...</Typography>
                ) : integrationData.length === 0 ? (
                  <Alert severity="info">{t('entities.noPaymentPages')}</Alert>
                ) : (
                  integrationData.map((page: any, idx: number) => (
                    <Card key={idx} variant="outlined" sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                          {page.titulo}
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableBody>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600, width: 280 }}>Pagali gateway payment page ID</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <code>{page.paymentPageId}</code>
                                    <IconButton size="small" onClick={() => { navigator.clipboard.writeText(page.paymentPageId); setSuccessMsg('Copiado!'); }}>
                                      <ContentCopy fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>Pagali gateway entity ID</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <code>{page.entityId}</code>
                                    <IconButton size="small" onClick={() => { navigator.clipboard.writeText(page.entityId); setSuccessMsg('Copiado!'); }}>
                                      <ContentCopy fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>URL</TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <code>{page.ecommerceUrl}</code>
                                    <IconButton size="small" onClick={() => { navigator.clipboard.writeText(page.ecommerceUrl); setSuccessMsg('Copiado!'); }}>
                                      <ContentCopy fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>URL Gateway (WooCommerce / API)</TableCell>
                                <TableCell>
                                  {(() => {
                                    // Cai para SANDBOX, como os outros dez sítios que
                                    // resolvem a base. Este era o único a cair para
                                    // produção — e é um URL que se copia e se cola numa
                                    // loja WooCommerce, portanto o erro não ficava no
                                    // ecrã: um comerciante da sandbox levava o endpoint
                                    // de produção para o site dele. Na falta da
                                    // variável, o destino errado tem de ser o inofensivo.
                                    const apiBase = process.env.REACT_APP_API_URL || 'https://europe-west1-pagali-sandbox.cloudfunctions.net/api';
                                    const apiVersion = process.env.REACT_APP_API_VERSION || 'v1';
                                    const gatewayUrl = `${apiBase}/${apiVersion}/public/ecommerce/payment`;
                                    return (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <code>{gatewayUrl}</code>
                                        <IconButton size="small" onClick={() => { navigator.clipboard.writeText(gatewayUrl); setSuccessMsg('Copiado!'); }}>
                                          <ContentCopy fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    );
                                  })()}
                                </TableCell>
                              </TableRow>
                              <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>{t('common.status')}</TableCell>
                                <TableCell>
                                  <Chip label={page.estado === 'A' ? t('common.active') : t('common.inactive')} color={page.estado === 'A' ? 'success' : 'default'} size="small" />
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                        <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant="outlined"
                            color={page.estado === 'A' ? 'warning' : 'success'}
                            onClick={async () => {
                              const newEstado = page.estado === 'A' ? 'I' : 'A';
                              try {
                                await api.put(`/admin/payment-pages/${page.paymentPageId}`, { estado: newEstado });
                                setSuccessMsg(newEstado === 'A' ? 'Página activada' : 'Página desactivada');
                                if (selectedEntity) fetchIntegrationData(selectedEntity.id);
                              } catch { setErrorMsg('Erro ao alterar estado'); }
                            }}
                          >
                            {page.estado === 'A' ? t('clients.deactivate') : t('clients.activate')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={async () => {
                              if (!window.confirm(`Apagar página "${page.titulo}"?`)) return;
                              try {
                                await api.delete(`/admin/payment-pages/${page.paymentPageId}`);
                                setSuccessMsg('Página apagada');
                                if (selectedEntity) fetchIntegrationData(selectedEntity.id);
                              } catch { setErrorMsg('Erro ao apagar'); }
                            }}
                          >
                            {t('common.delete')}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))
                )}

                {/* ── Webhook de Notificação ── */}
                <Box sx={{ mt: 4 }}>
                  <Divider sx={{ mb: 2 }} />
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <VpnKey fontSize="small" color="primary" />
                    Webhook de Notificação
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Configure o endpoint que recebe notificações de pagamento (HMAC-SHA256). O URL deve coincidir com o <code>notifyUrl</code> configurado nos pagamentos.
                  </Typography>

                  {webhookLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Card variant="outlined">
                      <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {/* URL */}
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                            <TextField
                              label="URL de callback"
                              placeholder="https://merchant.com/pagali/callback"
                              value={webhookUrl}
                              onChange={e => setWebhookUrl(e.target.value)}
                              fullWidth
                              size="small"
                              InputProps={{
                                startAdornment: <InputAdornment position="start"><LinkIcon fontSize="small" /></InputAdornment>,
                              }}
                            />
                            <Tooltip title="Detectar URL a partir de pagamentos recentes">
                              <IconButton
                                size="small"
                                sx={{ mt: 0.5 }}
                                onClick={e => fetchKnownUrls(e.currentTarget as HTMLButtonElement)}
                              >
                                <SearchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>

                          {/* Popover com URLs detectadas */}
                          <Popover
                            open={Boolean(urlPopoverAnchor)}
                            anchorEl={urlPopoverAnchor}
                            onClose={() => setUrlPopoverAnchor(null)}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          >
                            <Box sx={{ p: 1, minWidth: 320, maxWidth: 480 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                                notifyUrl encontrados nos últimos 90 dias (clica para usar)
                              </Typography>
                              {loadingKnownUrls ? (
                                <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} /></Box>
                              ) : knownUrls.length === 0 ? (
                                <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>Nenhum URL encontrado</Typography>
                              ) : (
                                <MuiList dense disablePadding>
                                  {knownUrls.map((u: any) => (
                                    <ListItemButton
                                      key={u.url}
                                      onClick={() => { setWebhookUrl(u.url); setUrlPopoverAnchor(null); }}
                                      sx={{ borderRadius: 1 }}
                                    >
                                      <ListItemText
                                        primary={u.url}
                                        secondary={`${u.total} pagamentos · último: ${new Date(u.ultimo).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' })}`}
                                        primaryTypographyProps={{ variant: 'body2', sx: { wordBreak: 'break-all' } }}
                                        secondaryTypographyProps={{ variant: 'caption' }}
                                      />
                                    </ListItemButton>
                                  ))}
                                </MuiList>
                              )}
                            </Box>
                          </Popover>

                          {/* Secret */}
                          {webhookConfig && (
                            <TextField
                              label="Signing Secret"
                              value={webhookSecretVisible && webhookRevealedSecret
                                ? webhookRevealedSecret
                                : (webhookConfig?.SECRET || '••••••••••••••••••••')}
                              fullWidth
                              size="small"
                              InputProps={{
                                readOnly: true,
                                startAdornment: <InputAdornment position="start"><VpnKey fontSize="small" /></InputAdornment>,
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {webhookSecretVisible && webhookRevealedSecret ? (
                                      <Tooltip title="Copiar secret">
                                        <IconButton size="small" onClick={() => { navigator.clipboard.writeText(webhookRevealedSecret); setSuccessMsg('Secret copiado!'); }}>
                                          <ContentCopy fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    ) : (
                                      <Tooltip title="Revelar secret">
                                        <IconButton size="small" onClick={revealWebhookSecret}>
                                          <Visibility fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {webhookSecretVisible && (
                                      <Tooltip title="Ocultar">
                                        <IconButton size="small" onClick={() => { setWebhookSecretVisible(false); setWebhookRevealedSecret(''); }}>
                                          <VisibilityOff fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />
                          )}

                          {/* Active toggle + buttons */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <FormControlLabel
                              control={<Switch checked={webhookAtivo} onChange={e => setWebhookAtivo(e.target.checked)} size="small" />}
                              label="Activo"
                            />
                            <Box sx={{ flexGrow: 1 }} />
                            {webhookConfig && (
                              <Tooltip title="Gerar novo secret (invalida o actual)">
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    startIcon={regenerating ? <CircularProgress size={14} /> : <Autorenew />}
                                    onClick={regenerateWebhookSecret}
                                    disabled={regenerating || webhookSaving}
                                  >
                                    Novo Secret
                                  </Button>
                                </span>
                              </Tooltip>
                            )}
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={webhookSaving ? <CircularProgress size={14} /> : <Save />}
                              onClick={saveWebhookConfig}
                              disabled={webhookSaving || !webhookUrl.trim()}
                            >
                              {webhookConfig ? 'Actualizar' : 'Configurar Webhook'}
                            </Button>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  )}

                  {/* Delivery history */}
                  {webhookDeliveries.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Últimas Entregas</Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Order</TableCell>
                              <TableCell>Status</TableCell>
                              <TableCell>HTTP</TableCell>
                              <TableCell>Duração</TableCell>
                              <TableCell>Data</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {webhookDeliveries.map((d: any) => (
                              <TableRow key={d.ID}>
                                <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{d.ORDER_ID || '—'}</TableCell>
                                <TableCell><Chip label={d.STATUS || '?'} size="small" color={d.SUCCESS ? 'success' : 'error'} /></TableCell>
                                <TableCell>{d.RESPONSE_CODE || '—'}</TableCell>
                                <TableCell>{d.DURATION_MS != null ? `${d.DURATION_MS}ms` : '—'}</TableCell>
                                <TableCell sx={{ fontSize: 11 }}>{d.DT_ENTREGA ? new Date(d.DT_ENTREGA).toLocaleString('pt-CV', { timeZone: 'Atlantic/Cape_Verde' }) : '—'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  )}
                </Box>

                {/* Dialog — consolidar páginas de pagamento */}
                <Dialog
                  open={consolidateDialogOpen}
                  onClose={() => setConsolidateDialogOpen(false)}
                  maxWidth="xs"
                  fullWidth
                >
                  <DialogTitle>
                    {integrationData.length > 1 ? 'Consolidar páginas de pagamento' : 'Renomear página de pagamento'}
                  </DialogTitle>
                  <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {integrationData.length > 1
                        ? <>Mantém apenas a página mais recente e <strong>elimina permanentemente</strong> todas as restantes da base de dados.</>
                        : 'Renomeia a página de pagamento para corresponder ao nome da entidade.'}
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 1 }}>
                      Nome: <strong>{selectedEntity?.desig}</strong>
                    </Typography>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setConsolidateDialogOpen(false)}>Cancelar</Button>
                    <Button
                      variant="contained"
                      startIcon={<CallMerge />}
                      onClick={handleConsolidate}
                      disabled={consolidating}
                    >
                      {consolidating ? 'A processar...' : (integrationData.length > 1 ? 'Consolidar' : 'Renomear')}
                    </Button>
                  </DialogActions>
                </Dialog>
              </TabPanel>

              {/* Extrato Tab */}
              <TabPanel value={viewTab} index={6}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom>{t('entities.extratoTitle')}</Typography>
                  <Typography variant="body2" color="text.secondary">{t('entities.extratoSubtitle')}</Typography>
                </Box>

                {/* Stats */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { label: t('entities.totalTransacoes'), value: extratoStats.totalTransacoes, color: 'primary.main', isCurrency: false },
                    { label: t('entities.grossAmount'), value: extratoStats.totalBruto, color: 'success.main', isCurrency: true },
                    { label: t('entities.commission'), value: extratoStats.totalComissao, color: 'warning.main', isCurrency: true },
                    { label: t('entities.ivaOnCommission'), value: extratoStats.totalIva, color: 'info.main', isCurrency: true },
                    { label: t('entities.netAmount'), value: extratoStats.totalLiquido, color: 'secondary.main', isCurrency: true },
                  ].map((stat) => (
                    <Grid key={stat.label} size={{ xs: 6, sm: 4, md: 2.4 }}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center', py: 1.5, '&:last-child': { pb: 1.5 } }}>
                          <Typography variant="h6" fontWeight={700} color={stat.color}>
                            {stat.isCurrency ? formatCurrency(stat.value as number) : stat.value}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Table */}
                {extratoLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : extratoData.length === 0 ? (
                  <Alert severity="info">{t('entities.noExtrato')}</Alert>
                ) : (
                  <>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: 'action.hover' }}>
                            <TableCell>{t('common.date')}</TableCell>
                            <TableCell>{t('common.reference')}</TableCell>
                            <TableCell>{t('common.service')}</TableCell>
                            <TableCell align="right">{t('entities.grossAmount')}</TableCell>
                            <TableCell align="right">{t('entities.commission')}</TableCell>
                            <TableCell align="right">{t('entities.ivaOnCommission')}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{t('entities.netAmount')}</TableCell>
                            <TableCell align="center">{t('common.status')}</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {extratoData.map((row: any) => (
                            <TableRow key={row.id} hover>
                              <TableCell sx={{ fontSize: 12 }}>
                                {row.dataRegisto ? new Date(row.dataRegisto).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' }) : '-'}
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{row.referencia}</TableCell>
                              <TableCell sx={{ fontSize: 12 }}>{row.servico}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12 }}>{formatCurrency(row.montante)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12, color: 'warning.main' }}>{formatCurrency(row.comissao)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12, color: 'info.main' }}>{formatCurrency(row.ivaComissao)}</TableCell>
                              <TableCell align="right" sx={{ fontSize: 12, fontWeight: 700, color: 'success.main' }}>{formatCurrency(row.valorLiquido)}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={row.estado === 'N' ? 'Pago' : row.estado === 'S' ? 'Pendente' : 'Falhado'}
                                  color={row.estado === 'N' ? 'success' : row.estado === 'S' ? 'warning' : 'error'}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 1.5 }}>
                      <Button size="small" disabled={extratoPage === 0} onClick={() => setExtratoPage(p => p - 1)}>&lt;</Button>
                      <Typography variant="caption">
                        {extratoPage + 1} / {Math.max(1, Math.ceil(extratoTotal / 15))} ({extratoTotal} {t('common.records')})
                      </Typography>
                      <Button size="small" disabled={(extratoPage + 1) * 15 >= extratoTotal} onClick={() => setExtratoPage(p => p + 1)}>&gt;</Button>
                    </Box>
                  </>
                )}
              </TabPanel>

              {/* Liquidações Tab */}
              <TabPanel value={viewTab} index={7}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" gutterBottom>{t('entities.liquidacoesTitle')}</Typography>
                    <Typography variant="body2" color="text.secondary">{t('entities.liquidacoesSubtitle')}</Typography>
                  </Box>
                  <Button variant="contained" size="small" startIcon={<Add />} onClick={() => {
                    const today = todayCv();
                    setLiquidacaoForm({ montante: '', dtLiquidacao: today, dtPeriodoIni: today, dtPeriodoFim: today, referencia: '', notas: '' });
                    setLiquidacaoFormOpen(true);
                  }}>
                    {t('entities.liquidacaoCreate')}
                  </Button>
                </Box>

                {/* Stats */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {[
                    { label: t('entities.totalLiquidacoes'), value: liquidacoesStats.total, isCurrency: false, color: 'primary.main' },
                    { label: t('entities.totalLiquidado'), value: liquidacoesStats.totalLiquidado, isCurrency: true, color: 'success.main' },
                    { label: t('entities.totalPendenteLiq'), value: liquidacoesStats.totalPendente, isCurrency: true, color: 'warning.main' },
                  ].map((stat) => (
                    <Grid key={stat.label} size={{ xs: 12, sm: 4 }}>
                      <Card variant="outlined">
                        <CardContent sx={{ textAlign: 'center' }}>
                          <Typography variant="h5" fontWeight={700} color={stat.color}>
                            {stat.isCurrency ? formatCurrency(stat.value as number) : stat.value}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">{stat.label}</Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                {/* Create form inline */}
                {liquidacaoFormOpen && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
                    <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{t('entities.liquidacaoCreate')}</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField fullWidth size="small" label={t('entities.liquidacaoMontante')} type="number"
                          value={liquidacaoForm.montante}
                          onChange={(e) => setLiquidacaoForm({ ...liquidacaoForm, montante: e.target.value })} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField fullWidth size="small" label={t('common.date')} type="date"
                          value={liquidacaoForm.dtLiquidacao}
                          onChange={(e) => setLiquidacaoForm({ ...liquidacaoForm, dtLiquidacao: e.target.value })}
                          slotProps={{ inputLabel: { shrink: true } }} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 4 }}>
                        <TextField fullWidth size="small" label={t('common.reference')} value={liquidacaoForm.referencia}
                          onChange={(e) => setLiquidacaoForm({ ...liquidacaoForm, referencia: e.target.value })} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth size="small" label={t('entities.liquidacaoPeriodoIni')} type="date"
                          value={liquidacaoForm.dtPeriodoIni}
                          onChange={(e) => setLiquidacaoForm({ ...liquidacaoForm, dtPeriodoIni: e.target.value })}
                          slotProps={{ inputLabel: { shrink: true } }} />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField fullWidth size="small" label={t('entities.liquidacaoPeriodoFim')} type="date"
                          value={liquidacaoForm.dtPeriodoFim}
                          onChange={(e) => setLiquidacaoForm({ ...liquidacaoForm, dtPeriodoFim: e.target.value })}
                          slotProps={{ inputLabel: { shrink: true } }} />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField fullWidth size="small" label={t('common.notes')} multiline rows={2}
                          value={liquidacaoForm.notas}
                          onChange={(e) => setLiquidacaoForm({ ...liquidacaoForm, notas: e.target.value })} />
                      </Grid>
                    </Grid>
                    <Box sx={{ display: 'flex', gap: 1, mt: 2, justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => setLiquidacaoFormOpen(false)}>{t('common.cancel')}</Button>
                      <Button size="small" variant="contained" disabled={savingLiquidacao || !liquidacaoForm.montante || !liquidacaoForm.dtLiquidacao}
                        onClick={handleSaveLiquidacao}>
                        {savingLiquidacao ? t('common.saving') : t('common.save')}
                      </Button>
                    </Box>
                  </Paper>
                )}

                {/* Table */}
                {liquidacoesLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                ) : liquidacoesData.length === 0 ? (
                  <Alert severity="info">{t('entities.noLiquidacoes')}</Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell>{t('common.date')}</TableCell>
                          <TableCell>{t('entities.liquidacaoPeriodo')}</TableCell>
                          <TableCell>{t('common.reference')}</TableCell>
                          <TableCell align="right">{t('entities.liquidacaoMontante')}</TableCell>
                          <TableCell align="center">{t('common.status')}</TableCell>
                          <TableCell align="center">{t('common.actions')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {liquidacoesData.map((liq: any) => (
                          <TableRow key={liq.id} hover>
                            <TableCell sx={{ fontSize: 12 }}>{liq.dtLiquidacao || '-'}</TableCell>
                            <TableCell sx={{ fontSize: 12 }}>
                              {liq.dtPeriodoIni && liq.dtPeriodoFim ? `${liq.dtPeriodoIni} → ${liq.dtPeriodoFim}` : '-'}
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{liq.referencia || '-'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>{formatCurrency(liq.montante)}</TableCell>
                            <TableCell align="center">
                              <Chip
                                label={liq.estado === 'C' ? t('entities.liquidacaoEstadoConcluido') : liq.estado === 'A' ? t('entities.liquidacaoEstadoAnulado') : t('entities.liquidacaoEstadoPendente')}
                                color={liq.estado === 'C' ? 'success' : liq.estado === 'A' ? 'error' : 'warning'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              {liq.estado === 'P' && (
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                  <Button size="small" variant="outlined" color="success" sx={{ fontSize: 10, py: 0.5 }}
                                    onClick={() => handleUpdateLiquidacaoEstado(liq.id, 'C')}>
                                    {t('entities.liquidacaoConcluir')}
                                  </Button>
                                  <Button size="small" variant="outlined" color="error" sx={{ fontSize: 10, py: 0.5 }}
                                    onClick={() => handleUpdateLiquidacaoEstado(liq.id, 'A')}>
                                    {t('entities.liquidacaoAnular')}
                                  </Button>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </TabPanel>

            </>
          )}
        </DialogContent>
        <DialogActions>
          {editMode ? (
            <>
              <Button onClick={handleCancelEdit}>{t('common.cancel')}</Button>
              <Button variant="contained" startIcon={<Save />} onClick={handleSaveEdit} disabled={savingEdit}>
                {savingEdit ? t('common.saving') : t('common.save')}
              </Button>
            </>
          ) : (
            <>
              <Button onClick={handleCloseView}>{t('common.close')}</Button>
              <Button
                variant="outlined"
                startIcon={<PowerSettingsNew />}
                color={selectedEntity?.estado === 'A' ? 'error' : 'success'}
                onClick={handleToggleStatus}
              >
                {selectedEntity?.estado === 'A' ? t('clients.deactivate') : t('clients.activate')}
              </Button>
              <Button variant="contained" startIcon={<Edit />} onClick={handleStartEdit}>
                {t('common.edit')}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Service Dialog */}
      <Dialog open={addServiceDialogOpen} onClose={() => setAddServiceDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('entities.addService', 'Adicionar Serviço')}</DialogTitle>
        <DialogContent sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('entities.addServiceHint', 'Seleciona o tipo de serviço a disponibilizar no portal desta entidade.')}
          </Typography>

          {/* Um selector vazio sem explicação deixa quem o usa sem saída: não há
              nada para escolher e o botão fica desactivado, sem dizer porquê. */}
          {serviceTypes.length === 0 && (
            <Alert severity="warning">
              Não há tipos de serviço registados. Cria-os em{' '}
              <strong>Configurações → Serviços → Gerir Tipos</strong> e volta aqui.
            </Alert>
          )}
          {tarifariosFalharam && (
            <Alert severity="error">
              Não foi possível carregar os tarifários. A lista aparece vazia por avaria,
              não por não existirem — o tarifário é opcional, podes continuar sem ele.
            </Alert>
          )}

          <TextField
            select
            fullWidth
            size="small"
            label={t('common.type', 'Tipo de Serviço')}
            value={selectedServiceTypeId}
            onChange={(e) => setSelectedServiceTypeId(e.target.value)}
          >
            {serviceTypes.map((st: any) => (
              <MenuItem key={st.id} value={st.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{st.desig}</Typography>
                  <Typography variant="caption" color="text.secondary">{st.codigo}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            fullWidth
            size="small"
            label="Tarifário (opcional)"
            value={selectedTarifarioId}
            onChange={(e) => setSelectedTarifarioId(e.target.value)}
          >
            <MenuItem value="">— Sem tarifário —</MenuItem>
            {availableTariffs.filter((t: any) => t.status === 'active').map((tar: any) => (
              <MenuItem key={tar.id} value={tar.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{tar.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{tar.code}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddServiceDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!selectedServiceTypeId || addingService}
            onClick={handleAddService}
          >
            {addingService ? <CircularProgress size={18} /> : t('common.add', 'Adicionar')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Tariff Dialog */}
      <Dialog open={editTariffDialog} onClose={() => setEditTariffDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Alterar Tarifário — {editingService?.nome}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {editingService?.cobranca?.origem === 'CONTRATO' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Este serviço tem contrato em vigor (v{editingService.cobranca.contrato?.versao}).
              O tarifário fica guardado como molde, mas <b>não muda o que se cobra</b> — para
              isso cria-se uma nova versão do contrato.
            </Alert>
          )}
          <TextField
            select
            fullWidth
            size="small"
            label="Tarifário"
            value={editTarifarioId}
            onChange={(e) => setEditTarifarioId(e.target.value)}
          >
            <MenuItem value="">— Sem tarifário —</MenuItem>
            {availableTariffs.filter((t: any) => t.status === 'active').map((tar: any) => (
              <MenuItem key={tar.id} value={tar.id}>
                <Box>
                  <Typography variant="body2" fontWeight={500}>{tar.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{tar.code}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTariffDialog(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" disabled={savingTariff} onClick={handleSaveTariff}>
            {savingTariff ? <CircularProgress size={18} /> : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog} onClose={() => setResetDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white' }}>
          {t('entities.passwordReset')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 3 }}>
            {t('entities.passwordResetWarning')}
          </Alert>
          {resetCredentials && (
            <Grid container spacing={2} sx={{ mt: 0 }}>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label={t('common.username')} value={resetCredentials.username || ''} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label={t('common.email')} value={resetCredentials.email || ''} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth label={t('common.password')} value={resetCredentials.password || ''} InputProps={{ readOnly: true }} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button startIcon={<ContentCopy />} onClick={() => {
            if (resetCredentials) {
              navigator.clipboard.writeText(`Username: ${resetCredentials.username}\nEmail: ${resetCredentials.email}\nPassword: ${resetCredentials.password}`);
              setSuccessMsg(t('entities.credentialsCopied'));
            }
          }}>
            {t('entities.copy')}
          </Button>
          <Button variant="contained" onClick={() => setResetDialog(false)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Entity Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.default', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}><Add /></Avatar>
            <Box>
              <Typography variant="h6" fontWeight={700}>{t('entities.newEntity')}</Typography>
              <Typography variant="caption" color="text.secondary">
                Preencha os dados da entidade. Campos com * são obrigatórios.
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, bgcolor: 'action.hover' }}>
          {/* Section: Dados da Entidade */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.light', width: 32, height: 32 }}><Business sx={{ fontSize: 18 }} /></Avatar>
              <Typography variant="subtitle1" fontWeight={600}>Dados da Entidade</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 8 }}>
                <TextField fullWidth size="small" label="Designação *"
                  placeholder="Ex: Odjanu Optics, CVMA, Electra SA"
                  value={createForm.desig}
                  onChange={(e) => setCreateForm({ ...createForm, desig: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth size="small" select label="Perfil de Conta"
                  value={createForm.perfilCodigo}
                  onChange={(e) => {
                    const perfil = e.target.value;
                    const tipoCodigo = PERFIL_TIPO_MAP[perfil] || '';
                    const tipoMatch = entityTypes.find(t => t.codigo === tipoCodigo);
                    setCreateForm({ ...createForm, perfilCodigo: perfil, tipoId: tipoMatch?.id || '' });
                  }}>
                  <MenuItem value="">-</MenuItem>
                  {[
                    { code: 'PFLOJ', label: 'Loja/Comércio' },
                    { code: 'PFEMP', label: 'Empresa' },
                    { code: 'PFGOV', label: 'Governo' },
                    { code: 'PFONG', label: 'ONG/Associação' },
                    { code: 'PFEVT', label: 'Promotor de Eventos' },
                    { code: 'PFPRO', label: 'Profissional Liberal' },
                    { code: 'PFTUR', label: 'Promotor Turístico' },
                  ].map(p => <MenuItem key={p.code} value={p.code}>{p.label}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth size="small" select label="Tipo de Entidade"
                  value={createForm.tipoId}
                  onChange={(e) => setCreateForm({ ...createForm, tipoId: e.target.value })}>
                  <MenuItem value="">-</MenuItem>
                  {entityTypes.map((t) => <MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth size="small" label="NIF"
                  placeholder="Número de identificação fiscal"
                  value={createForm.nif}
                  onChange={(e) => setCreateForm({ ...createForm, nif: e.target.value.replace(/[^0-9]/g, '') })} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth size="small" label="Taxa de Transação (%)"
                  placeholder="0.00"
                  value={createForm.taxaTransacao}
                  onChange={(e) => setCreateForm({ ...createForm, taxaTransacao: e.target.value.replace(/[^0-9.]/g, '') })}
                  helperText="Percentagem cobrada por transação (ex: 2.5)" />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <TextField fullWidth size="small" multiline rows={2} label="Descrição"
                  placeholder="Breve descrição da entidade / área de atuação"
                  value={createForm.descricao}
                  onChange={(e) => setCreateForm({ ...createForm, descricao: e.target.value })} />
              </Grid>
            </Grid>
          </Paper>

          {/* Section: Contactos */}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar sx={{ bgcolor: 'info.light', width: 32, height: 32 }}><ContactMail sx={{ fontSize: 18 }} /></Avatar>
              <Typography variant="subtitle1" fontWeight={600}>Contactos</Typography>
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth size="small" label="Nome do Responsável"
                  placeholder="Diretor / Proprietário"
                  value={createForm.nomeResp}
                  onChange={(e) => setCreateForm({ ...createForm, nomeResp: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <TextField fullWidth size="small" label="Pessoa de Contacto"
                  placeholder="Nome do contacto operacional"
                  value={createForm.nomeContacto}
                  onChange={(e) => setCreateForm({ ...createForm, nomeContacto: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth size="small" label="Telefone"
                  placeholder="+238 000 0000"
                  value={createForm.telefone}
                  onChange={(e) => setCreateForm({ ...createForm, telefone: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth size="small" type="email" label="Email Institucional"
                  placeholder="geral@entidade.cv"
                  value={createForm.emailEntidade}
                  onChange={(e) => setCreateForm({ ...createForm, emailEntidade: e.target.value })} />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <TextField fullWidth size="small" label="Website"
                  placeholder="https://www.entidade.cv"
                  value={createForm.website}
                  onChange={(e) => setCreateForm({ ...createForm, website: e.target.value })} />
              </Grid>
            </Grid>
          </Paper>

          {/* Section: Localização + Bancário (side-by-side em desktop) */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 7 }}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'warning.light', width: 32, height: 32 }}><LocationOn sx={{ fontSize: 18 }} /></Avatar>
                  <Typography variant="subtitle1" fontWeight={600}>Localização</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth size="small" label="Morada"
                      placeholder="Rua / Avenida, número, bairro"
                      value={createForm.morada}
                      onChange={(e) => setCreateForm({ ...createForm, morada: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth size="small" select label="Ilha / Concelho"
                      value={createForm.geografiaId}
                      onChange={(e) => setCreateForm({ ...createForm, geografiaId: e.target.value })}>
                      <MenuItem value="">-</MenuItem>
                      {geografiaList.map((g) => (
                        <MenuItem key={g.id} value={g.id}>
                          {g.ilha} — {g.concelho}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.light', width: 32, height: 32 }}><AccountBalance sx={{ fontSize: 18 }} /></Avatar>
                  <Typography variant="subtitle1" fontWeight={600}>Dados Bancários</Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth size="small" label="Banco"
                      placeholder="BCA, BCN, Caixa..."
                      value={createForm.banco}
                      onChange={(e) => setCreateForm({ ...createForm, banco: e.target.value })} />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth size="small" label="Nº de Conta"
                      placeholder="IBAN ou nº da conta"
                      value={createForm.numConta}
                      onChange={(e) => setCreateForm({ ...createForm, numConta: e.target.value })} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          {/* Section: Conta de Administração */}
          <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Avatar sx={{ bgcolor: 'secondary.light', width: 32, height: 32 }}><AdminPanelSettings sx={{ fontSize: 18 }} /></Avatar>
              <Typography variant="subtitle1" fontWeight={600}>Conta de Administração</Typography>
            </Box>

            {selectedUser ? (
              /* Utilizador existente seleccionado */
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, border: 1, borderColor: 'success.main', borderRadius: 2, bgcolor: 'success.50' }}>
                <Avatar sx={{ bgcolor: 'success.main', width: 36, height: 36 }}>
                  <PersonSearch sx={{ fontSize: 20 }} />
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600}>{selectedUser.nome}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>{selectedUser.email}</Typography>
                </Box>
                <Chip label="Conta existente" color="success" size="small" />
                <IconButton size="small" onClick={() => { setSelectedUser(null); setUserSearchInput(''); }}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              /* Pesquisa / criação de nova conta */
              <Autocomplete
                freeSolo
                options={userSearchOptions}
                loading={userSearchLoading}
                inputValue={userSearchInput}
                onInputChange={(_, val) => setUserSearchInput(val)}
                onChange={(_, val) => {
                  if (val && typeof val === 'object') setSelectedUser(val as any);
                }}
                getOptionLabel={(opt) => typeof opt === 'string' ? opt : `${(opt as any).nome} (${(opt as any).email})`}
                renderOption={(props, opt: any) => (
                  <li {...props} key={opt.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
                      <Avatar sx={{ width: 30, height: 30, fontSize: 13, bgcolor: 'primary.light' }}>
                        {opt.nome?.[0]?.toUpperCase() || '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{opt.nome}</Typography>
                        <Typography variant="caption" color="text.secondary">{opt.email}</Typography>
                      </Box>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Conta de administrador"
                    placeholder="Pesquisar utilizador existente por email ou nome..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <PersonAdd sx={{ mr: 1, color: 'text.disabled', fontSize: 20 }} />,
                      endAdornment: (
                        <>
                          {userSearchLoading ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    helperText={
                      userSearchInput.length >= 2 && userSearchOptions.length === 0 && !userSearchLoading
                        ? 'Nenhuma conta encontrada — será criada uma conta nova automaticamente.'
                        : 'Pesquise uma conta existente para associar, ou deixe em branco para criar uma nova.'
                    }
                  />
                )}
                noOptionsText="Nenhuma conta encontrada — será criada uma nova"
                loadingText="A pesquisar..."
              />
            )}

            <Alert severity="info" variant="outlined" sx={{ fontSize: 13, mt: 2 }}>
              {selectedUser
                ? 'A entidade ficará associada à conta seleccionada. Não serão geradas credenciais novas.'
                : 'Ao criar a entidade, é gerada automaticamente uma página de pagamento e credenciais de admin.'}
            </Alert>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button onClick={() => setCreateDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSaveCreate} disabled={savingCreate || !createForm.desig.trim()}>
            {savingCreate ? t('common.creating') : 'Criar Entidade'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credentials Dialog - shown after entity creation */}
      <Dialog open={credentialsDialog} onClose={() => setCredentialsDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
          {t('entities.entityCreated')}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('entities.credentialsWarning')}
          </Alert>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label={t('common.username')} value={credentials.username} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label={t('common.email')} value={credentials.email} InputProps={{ readOnly: true }} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth label={t('common.password')} value={credentials.password} InputProps={{ readOnly: true }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            navigator.clipboard.writeText(`Username: ${credentials.username}\nEmail: ${credentials.email}\nPassword: ${credentials.password}`);
            setSuccessMsg(t('entities.credentialsCopied'));
          }}>
            {t('entities.copy')}
          </Button>
          <Button variant="contained" onClick={() => setCredentialsDialog(false)}>
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Entities;
